import { useState } from "react"
import { useFirestore, useFirestoreCollectionData, useFirestoreDocData } from "reactfire"
import { collection, doc, setDoc } from "firebase/firestore"

function calcularTotales(pagos, cuotas) {
  const contables = pagos.filter(p => p.estado !== "no_aplica")
  const recaudado = contables.filter(p => p.estado === "pagado")
    .reduce((s, p) => s + (cuotas.find(c => c.id === p.cuotaId)?.monto || 0), 0)
  const pendiente = contables.filter(p => p.estado === "pendiente" || p.estado === "atrasado")
    .reduce((s, p) => s + (cuotas.find(c => c.id === p.cuotaId)?.monto || 0), 0)
  return { recaudado, pendiente, esperado: recaudado + pendiente }
}

const TIPO_CONFIG = {
  mensual: { icono: "📅", label: "Mensualidades", color: "var(--verde)", bg: "var(--verde-suave)" },
  evento:  { icono: "⛺", label: "Eventos",        color: "#92400e",      bg: "#fef3e2"           },
}

export default function ResumenFinanciero({ unidadId }) {
  const firestore = useFirestore()
  const base      = ["unidades", unidadId]

  const { data: apoderados } = useFirestoreCollectionData(collection(firestore, ...base, "apoderados"), { idField: "id" })
  const { data: cuotas }     = useFirestoreCollectionData(collection(firestore, ...base, "cuotas"),     { idField: "id" })
  const { data: pagos }      = useFirestoreCollectionData(collection(firestore, ...base, "pagos"),      { idField: "id" })
  const { data: unidadDoc }  = useFirestoreDocData(doc(firestore, "unidades", unidadId))
  const { data: egresos }    = useFirestoreCollectionData(collection(firestore, ...base, "egresos"),  { idField: "id" })
  const { data: ingresos }   = useFirestoreCollectionData(collection(firestore, ...base, "ingresos"), { idField: "id" })

  const todasCuotas     = cuotas     || []
  const todosPagos      = pagos      || []
  const todosApoderados = apoderados || []

  const saldoInicial      = unidadDoc?.saldoInicial ?? 0
  const totalEgresos      = (egresos  || []).reduce((s, e) => s + (e.monto || 0), 0)
  const totalIngresosExtra = (ingresos || []).reduce((s, i) => s + (i.monto || 0), 0)
  const apoderadosActivos = todosApoderados.filter(a => !a.exento)
  const totalExentos      = todosApoderados.filter(a => a.exento).length

  const tipos = ["mensual", "evento"]
  const totalesPorTipo = tipos.reduce((acc, tipo) => {
    const cc  = todasCuotas.filter(c => c.tipo === tipo)
    const ids = new Set(cc.map(c => c.id))
    acc[tipo] = { cuotas: cc, ...calcularTotales(todosPagos.filter(p => ids.has(p.cuotaId)), cc) }
    return acc
  }, {})

  const totalGeneral   = calcularTotales(todosPagos, todasCuotas)
  const totalRecaudado = totalGeneral.recaudado + saldoInicial + totalIngresosExtra - totalEgresos

  return (
    <div>
      {/* ── Saldo inicial ── */}
      <SaldoInicialCard
        unidadId={unidadId}
        saldoInicial={saldoInicial}
        firestore={firestore}
      />

      {/* Stats generales */}
      <div className="resumen-grid" style={{ marginBottom: 32 }}>
        {[
          { label: "Apoderados activos", valor: apoderadosActivos.length },
          { label: "Exentos",            valor: totalExentos             },
          { label: "Cuotas totales",     valor: todasCuotas.length       },
        ].map(({ label, valor }) => (
          <div key={label} className="stat-card">
            <div className="stat-label">{label}</div>
            <div className="stat-valor">{valor}</div>
          </div>
        ))}
      </div>

      {/* Desglose por tipo */}
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: "var(--verde)", fontSize: 18, marginBottom: 16 }}>
        Recaudación por tipo
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 }}>
        {tipos.map(tipo => {
          const cfg = TIPO_CONFIG[tipo]
          const t   = totalesPorTipo[tipo]
          if (t.cuotas.length === 0) return null
          const pct = t.esperado > 0 ? Math.round((t.recaudado / t.esperado) * 100) : 0
          return (
            <div key={tipo} className="card" style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: cfg.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                  {cfg.icono}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{cfg.label}</div>
                  <div style={{ fontSize: 12, color: "var(--gris)" }}>{t.cuotas.length} cuota{t.cuotas.length !== 1 ? "s" : ""}</div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontSize: 12, color: "var(--gris)", marginBottom: 2 }}>Recaudado</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: "#166534" }}>${t.recaudado.toLocaleString("es-CL")}</div>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ height: 8, borderRadius: 999, background: "var(--gris-borde)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: cfg.color, borderRadius: 999, transition: "width 0.6s ease" }} />
                </div>
                <div style={{ fontSize: 12, color: "var(--gris)", marginTop: 5, textAlign: "right" }}>{pct}% recaudado</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                {[
                  { label: "Recaudado",  valor: t.recaudado, bg: "#dcfce7",          color: "#166534"       },
                  { label: "Por cobrar", valor: t.pendiente, bg: "var(--rojo-suave)", color: "var(--rojo)"  },
                  { label: "Esperado",   valor: t.esperado,  bg: "var(--verde-suave)", color: "var(--verde)" },
                ].map(({ label, valor, bg, color }) => (
                  <div key={label} style={{ textAlign: "center", padding: "10px 8px", borderRadius: 10, background: bg }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, color }}>{label}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color }}>${valor.toLocaleString("es-CL")}</div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: "1px solid var(--gris-borde)", paddingTop: 12 }}>
                {t.cuotas.map(cuota => {
                  const tc = calcularTotales(todosPagos.filter(p => p.cuotaId === cuota.id), [cuota])
                  return (
                    <div key={cuota.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--gris-borde)", fontSize: 13 }}>
                      <span style={{ fontWeight: 500 }}>{cuota.descripcion}</span>
                      <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
                        <span style={{ color: "#166534", fontWeight: 600 }}>+${tc.recaudado.toLocaleString("es-CL")}</span>
                        {tc.pendiente > 0 && <span style={{ color: "var(--rojo)" }}>-${tc.pendiente.toLocaleString("es-CL")}</span>}
                        <span style={{ color: "var(--gris)" }}>${cuota.monto.toLocaleString("es-CL")} c/u</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {/* Total general — incluye saldo inicial */}
      <div style={{ background: "var(--verde)", borderRadius: 16, padding: "24px 28px", marginBottom: 36 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 20, marginBottom: 12 }}>
          {[
            { label: "Saldo inicial",    valor: saldoInicial,           color: "#86efac" },
            { label: "Recaudado cuotas", valor: totalGeneral.recaudado,  color: "#86efac" },
            { label: "Ingresos extra",    valor: totalIngresosExtra,     color: "#86efac" },
            { label: "Por cobrar",       valor: totalGeneral.pendiente,  color: "#fca5a5" },
            { label: "Egresos",          valor: totalEgresos,            color: "#fca5a5" },
            { label: "Total en caja",    valor: totalRecaudado,          color: "#fff"    },
          ].map(({ label, valor, color }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(255,255,255,0.55)", marginBottom: 6 }}>{label}</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color }}>${valor.toLocaleString("es-CL")}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.5)", borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 10 }}>
          Total en caja = saldo inicial + recaudado cuotas + ingresos extra − egresos = ${totalRecaudado.toLocaleString("es-CL")}
        </div>
      </div>

      {/* Detalle por apoderado */}
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: "var(--verde)", fontSize: 18, marginBottom: 16 }}>Detalle por apoderado</h3>
      <div className="tabla-wrapper">
        <table>
          <thead>
            <tr>
              <th>Apoderado</th>
              <th>Scouts</th>
              <th>Pagado</th>
              <th>Por pagar</th>
              <th>Observaciones</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {apoderadosActivos.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--gris)", padding: 32 }}>No hay apoderados activos.</td></tr>
            ) : (
              apoderadosActivos.map(ap => (
                <FilaResumen
                  key={ap.id}
                  apoderado={ap}
                  pagos={todosPagos.filter(p => p.apoderadoId === ap.id)}
                  cuotas={todasCuotas}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* Card editable para el saldo inicial */
function SaldoInicialCard({ unidadId, saldoInicial, firestore }) {
  const [editando,  setEditando]  = useState(false)
  const [valor,     setValor]     = useState("")
  const [guardando, setGuardando] = useState(false)

  const abrir = () => {
    setValor(saldoInicial.toString())
    setEditando(true)
  }

  const guardar = async () => {
    const num = parseFloat(valor)
    if (isNaN(num) || num < 0) return
    setGuardando(true)
    await setDoc(doc(firestore, "unidades", unidadId), { saldoInicial: num }, { merge: true })
    setGuardando(false)
    setEditando(false)
  }

  return (
    <div className="card" style={{ padding: "20px 24px", marginBottom: 24, display: "flex", alignItems: "center", gap: 20 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--verde-suave)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
        🏦
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Saldo inicial de la unidad</div>
        <div style={{ fontSize: 13, color: "var(--gris)" }}>
          Monto en caja antes de las cuotas registradas en este sistema
        </div>
      </div>

      {editando ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="number"
            min="0"
            className="form-input"
            style={{ width: 160, margin: 0, padding: "8px 12px", fontSize: 15 }}
            value={valor}
            onChange={e => setValor(e.target.value)}
            onKeyDown={e => e.key === "Enter" && guardar()}
            autoFocus
          />
          <button className="btn-primary" style={{ padding: "8px 16px" }} onClick={guardar} disabled={guardando}>
            {guardando ? "..." : "Guardar"}
          </button>
          <button className="btn-secondary" style={{ padding: "8px 16px" }} onClick={() => setEditando(false)}>
            Cancelar
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: "var(--verde)" }}>
            ${saldoInicial.toLocaleString("es-CL")}
          </div>
          <button className="btn-icon" title="Editar saldo inicial" onClick={abrir}>✏️</button>
        </div>
      )}
    </div>
  )
}

function FilaResumen({ apoderado, pagos, cuotas }) {
  const contables      = pagos.filter(p => p.estado !== "no_aplica")
  const pagado         = contables.filter(p => p.estado === "pagado").reduce((s, p) => s + (cuotas.find(c => c.id === p.cuotaId)?.monto || 0), 0)
  const porPagar       = contables.filter(p => p.estado === "pendiente" || p.estado === "atrasado").reduce((s, p) => s + (cuotas.find(c => c.id === p.cuotaId)?.monto || 0), 0)
  const tieneAtrasado  = pagos.some(p => p.estado === "atrasado")
  const tienePendiente = pagos.some(p => p.estado === "pendiente")

  // Juntar todas las observaciones no vacías
  const observaciones = pagos
    .filter(p => p.observacion && p.observacion.trim())
    .map(p => p.observacion.trim())

  return (
    <tr>
      <td className="td-nombre">{apoderado.nombre}</td>
      <td className="td-scouts">{apoderado.scouts?.join(", ") || "—"}</td>
      <td style={{ color: "#166534", fontWeight: 600 }}>{pagado > 0 ? `$${pagado.toLocaleString("es-CL")}` : "—"}</td>
      <td style={{ color: porPagar > 0 ? "var(--rojo)" : "var(--gris)", fontWeight: porPagar > 0 ? 600 : 400 }}>{porPagar > 0 ? `$${porPagar.toLocaleString("es-CL")}` : "—"}</td>
      <td style={{ fontSize: 13, color: "var(--texto-suave)", fontStyle: observaciones.length ? "italic" : "normal" }}>
        {observaciones.length > 0 ? observaciones.join(" · ") : <span style={{ color: "var(--gris-borde)" }}>—</span>}
      </td>
      <td>
        {tieneAtrasado   ? <span className="badge badge-atrasado">⚠ Atrasado</span>
        : tienePendiente ? <span className="badge badge-pendiente">⏳ Pendiente</span>
        : contables.length > 0 ? <span className="badge badge-pagado">✓ Al día</span>
        : <span className="badge badge-exento">Sin cuotas</span>}
      </td>
    </tr>
  )
}
